import axios from 'axios'
import { BASE_URL } from '@/environment/environment'
import type { ReportFormat, ReportPreview } from '@/modules/reportTemplates/api'

export type BrandingProfileKey = 'official' | 'report' | 'spreadsheet'
export interface BrandingProfile {
  enabled: boolean
  font: 'inter' | 'dejavu'
  show_logo: boolean
  show_details: boolean
  primary: string
  accent: string
  pale: string
  margin_top: number
  margin_bottom: number
  margin_left: number
  margin_right: number
  footer: string
}
export type BrandingConfig = Record<BrandingProfileKey, BrandingProfile> & { adaptation_notes: string }
export interface BrandingReference { id: number; name: string; size: number; created_at: string }
export interface BrandingVersion { id: number; created_at: string; created_by: string; restored_from: number | null }
export interface BrandingState {
  revision: number
  draft: BrandingConfig
  defaults: BrandingConfig
  published_id: number | null
  versions: BrandingVersion[]
  references: BrandingReference[]
}
export interface BrandingSample { config: BrandingConfig; profile: BrandingProfileKey; format: ReportFormat }

const url = `${String(BASE_URL || '').replace(/\/$/, '')}/api/report-branding`
export const reportBrandingApi = {
  async get() { return (await axios.get<BrandingState>(`${url}/`)).data },
  async save(config: BrandingConfig, revision: number) {
    return (await axios.put<BrandingState>(`${url}/`, { config, revision })).data
  },
  async publish(config: BrandingConfig, revision: number) {
    return (await axios.post<BrandingState>(`${url}/publish/`, { config, revision })).data
  },
  async restore(id: number, revision: number) {
    return (await axios.post<BrandingState>(`${url}/versions/${id}/restore/`, { revision })).data
  },
  async preview(payload: BrandingSample, signal: AbortSignal) {
    return (await axios.post<ReportPreview>(`${url}/preview/`, payload, { signal })).data
  },
  async download(payload: BrandingSample) {
    return (await axios.post<Blob>(`${url}/preview/`, { ...payload, download: true }, { responseType: 'blob' })).data
  },
  async upload(file: File) {
    const body = new FormData()
    body.append('file', file)
    return (await axios.post<BrandingState>(`${url}/references/`, body)).data
  },
  async reference(id: number) {
    return (await axios.get<Blob>(`${url}/references/${id}/`, { responseType: 'blob' })).data
  },
  async removeReference(id: number) { await axios.delete(`${url}/references/${id}/`) },
}
